/**
 * Weld Mapping access for the PWA.
 *
 * The rule lives on the web (WeldLogTrait): weld_mapping org feature AND the
 * tenant-wide advanced_weldlog setting AND the project's own advanced_weldlog
 * flag. The first two are resolved server-side and delivered as the
 * `weld-mapping` permission by the common API; the per-project part arrives on
 * the advanced weld log entry of get_project_modules_list.
 *
 * Nothing here re-derives the rule — it only reads what the backend decided.
 * Unlike checkActionPermission(), a missing key is treated as NO access, so a
 * stale permission payload can never open the mapping flow.
 */
export const isWeldMappingEnabled = (permissions) => {
  const entry = permissions && permissions['weld-mapping']
  // Fail closed: an absent key means the backend did not grant it. Admins are
  // not special-cased — the org feature flag is already folded into `list`
  // server-side, and no role may bypass a feature the tenant does not have.
  if (!entry) return false
  return Boolean(entry.list)
}

/**
 * "Show the joints list" preference.
 *
 * Device-local view state, not user config: which panel a welder keeps open on
 * a shared shop-floor tablet is a property of that tablet's screen size and how
 * it is mounted, so it deliberately stays in localStorage rather than following
 * the user across devices the way the dashboard layout does.
 *
 * Defaults to shown, so nothing disappears for anyone who never touches it.
 */
export const SHOW_JOINTS_KEY = 'WelditPwa_wmShowJoints'

export function readShowJoints () {
  try {
    // Only an explicit opt-out hides the list; an absent or unreadable value
    // falls back to shown.
    return window.localStorage.getItem(SHOW_JOINTS_KEY) !== '0'
  } catch (e) {
    return true
  }
}

export function writeShowJoints (value) {
  try {
    window.localStorage.setItem(SHOW_JOINTS_KEY, value ? '1' : '0')
  } catch (e) {
    // Quota or private-mode failures are non-fatal: the toggle still works for
    // this session, it just will not be remembered.
  }
}

/**
 * Last production-series unit opened per drawing (Decision C).
 *
 * The URL (?unit_id=) is the primary carrier; this is the fallback when a
 * drawing is reached without one — the drawing pager, the overview, a
 * reload after the query was lost. Device-local for the same reason as the
 * joints toggle: it records where this tablet's crew is working.
 */
export const UNIT_KEY_PREFIX = 'WelditPwa_wmUnit:'

export function readUnit (drawingId) {
  try {
    return window.localStorage.getItem(UNIT_KEY_PREFIX + drawingId)
  } catch (e) {
    return null
  }
}

export function writeUnit (drawingId, unitId) {
  try {
    window.localStorage.setItem(UNIT_KEY_PREFIX + drawingId, String(unitId))
  } catch (e) {
    // Non-fatal, as above.
  }
}

export default {
  computed: {
    weldMappingEnabled () {
      const auth = this.$store && this.$store.state && this.$store.state.auth
      return isWeldMappingEnabled(auth && auth.permissions)
    }
  }
}
